/**
 * Main Application Entry Point
 *
 * This file initializes the Vue 3 application with all necessary dependencies:
 * - Vue app with Pinia state management
 * - PrimeVue UI component library with Aura theme
 * - Firebase Realtime Database with anonymous authentication
 * - Global application providers and configurations
 */

// Vue framework and state management imports
import {createApp} from "vue";
import {createPinia} from "pinia";
import App from "./App.vue";

// PrimeVue UI library imports
import PrimeVue from "primevue/config";
import Aura from "@primevue/themes/aura";
import "primeicons/primeicons.css";
import ConfirmationService from "primevue/confirmationservice";
import ConfirmDialog from "primevue/confirmdialog";

// Application styles
import "@/assets/tailwind.css";

// Firebase services imports
import {initializeApp} from "firebase/app";
import {getDatabase} from "firebase/database";
import {getAuth, signInAnonymously} from "firebase/auth";

console.log("started");

// =============================================================================
// FIREBASE CONFIGURATION AND INITIALIZATION
// =============================================================================

/**
 * Firebase project configuration
 * Contains all necessary keys and endpoints for connecting to the Firebase project
 * This configuration is safe to expose in client-side code as it's designed for public access
 */
const firebaseConfig = {
  apiKey            : "AIzaSyCvXGB8KGkOIqzoni_NEX3Tul2inI8SkR8",
  authDomain        : "cash-3cd0e.firebaseapp.com",
  projectId         : "cash-3cd0e",
  storageBucket     : "cash-3cd0e.appspot.com",
  messagingSenderId : "893572838289",
  appId             : "1:893572838289:web:4d2166209662354e9c2030",
  databaseURL       : "https://cash-3cd0e-default-rtdb.asia-southeast1.firebasedatabase.app"
};

// Initialize Firebase app instance
const firebaseApp = initializeApp(firebaseConfig);

// Get Firebase Realtime Database instance
const db = getDatabase(firebaseApp);

// =============================================================================
// FIREBASE AUTHENTICATION
// =============================================================================

/**
 * Initialize Firebase Authentication with anonymous sign-in
 * Anonymous authentication allows users to access the app without creating accounts
 * while still providing user identification for database security rules
 */
const auth = getAuth(firebaseApp);
try {
  const userCred = await signInAnonymously(auth);
  console.log("Signed in anonymously", userCred);
  // Authentication successful - user can now access Firebase services
} catch (error : any) {
  console.log(error.code, error.message);
}

// =============================================================================
// VUE APPLICATION SETUP
// =============================================================================

/**
 * Create and configure the main Vue application instance
 */
// Create Vue app
const app = createApp(App);

/**
 * Configure Pinia for state management
 * Pinia provides reactive stores for managing application state across components
 */
app.use(createPinia());

/**
 * Configure PrimeVue UI component library
 * - Uses Aura theme for modern, accessible component styling
 * - Provides comprehensive set of form controls, dialogs, and UI elements
 */
app.use(PrimeVue, {
  theme : {
    preset : Aura // Modern theme with clean design and good accessibility
  }
});

/**
 * Register global PrimeVue components
 * ConfirmDialog provides user confirmation prompts throughout the app
 */
app.component("ConfirmDialog", ConfirmDialog);

/**
 * Add confirmation service for programmatic dialog management
 * Allows components to show confirmation dialogs without managing dialog state
 */
app.use(ConfirmationService);

/**
 * Provide Firebase database instance to all components
 * This makes the database available via dependency injection in any component
 * Components can access it using: const db = inject("db") as Database
 */
app.provide("db", db);

/**
 * Mount the application to the DOM
 * Attaches the Vue app to the HTML element with id="app"
 */
app.mount("#app");
